// components/received/PasswordChangeModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';

const PasswordChangeModal = ({ show, onSuccess, onError, endpoints }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Reset form when modal is shown
  React.useEffect(() => {
    if (show) {
      setNewPassword('');
      setConfirmPassword('');
      setValidationErrors([]);
      
      // Always get the current password from login
      const loginPassword = sessionStorage.getItem('loginPassword');
      if (loginPassword) {
        setCurrentPassword(loginPassword);
      } else {
        setCurrentPassword('');
      }
    }
  }, [show]);
  
  // Validate password
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setValidationErrors(['Passwords do not match']);
      return;
    }
    
    // Validate password
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Check if current password is available
    if (!currentPassword.trim()) {
      setValidationErrors(['Current password is required. Please try logging in again.']);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get recipient info from session storage
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const username = recipientInfo.username;
      
      if (!username) {
        throw new Error('Username not found');
      }
      
      const requestBody = {
        username,
        currentPassword: currentPassword.trim(),
        newPassword
      };
      
      const response = await fetch(endpoints.CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody),
      });
      
      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (data.error) {
          onError(data.error);
        } else {
          // Update recipient info in session storage
          if (recipientInfo) {
            recipientInfo.isPasswordChanged = true;
            sessionStorage.setItem('recipientInfo', JSON.stringify(recipientInfo));
          }
          
          // Clear login password from session storage for security
          sessionStorage.removeItem('loginPassword');
          
          onSuccess();
        }
      } else {
        // Not JSON, log the text for debugging
        const text = await response.text();
        console.error('Non-JSON response:', text);
        onError('Unexpected response from server');
      }
    } catch (error) {
      console.error('Password change error:', error);
      onError(`Password change failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      className="re-password-change-modal"
    >
      <Modal.Header>
        <Modal.Title>
          Change Your Password
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          Please securely store your username and password. Never share them with anyone.
        </Alert>
        
        <Alert variant="info" className="mb-3">
          You need to set a new secure password for your account.
        </Alert>
        
        {validationErrors.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <ul className="mb-0 ps-3">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Current Password</Form.Label>
            <Form.Control
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setValidationErrors([]);
              }}
              required
              disabled={isLoading}
              placeholder="The password you just used to log in"
              readOnly={!!sessionStorage.getItem('loginPassword')}
            />
            {!!sessionStorage.getItem('loginPassword') && (
              <Form.Text className="text-muted">
                Using the password you just logged in with
              </Form.Text>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setValidationErrors([]);
              }}
              required
              disabled={isLoading}
            />
          </Form.Group>
          
          <Form.Group className="mb-4">
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setValidationErrors([]);
              }}
              required
              disabled={isLoading}
            />
          </Form.Group>
          
          <div className="password-requirements mb-4">
            <p className="mb-2 text-muted">Password requirements:</p>
            <ul className="text-muted small ps-3 mb-0">
              <li>At least 8 characters long</li>
              <li>At least one uppercase letter (A-Z)</li>
              <li>At least one lowercase letter (a-z)</li>
              <li>At least one number (0-9)</li>
              <li>At least one special character (!@#$%^&*)</li>
            </ul>
          </div>
          
          <Button
            type="submit"
            className="re-btn-primary w-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Changing Password...
              </>
            ) : (
              'Change Password'
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

PasswordChangeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  endpoints: PropTypes.object.isRequired
};

export default PasswordChangeModal;